// Frontend/src/api/settings.ts
/**
 * Settings API - HOA Admin profile and payment details
 */

import api from "./client";

export interface PaymentDetails {
    id: string;
    user_email: string;
    user_id: string;
    bank_name?: string;
    bank_account_number?: string;
    bank_routing_number?: string;
    account_holder_name?: string;
    zelle_email?: string;
    zelle_phone?: string;
    paypal_email?: string;
    venmo_username?: string;
    cashapp_username?: string;
    pago_movil_bank?: string;
    pago_movil_phone?: string;
    pago_movil_id_number?: string;
    cash_instructions?: string;
    is_verified: boolean;
    created_at: string;
    updated_at: string;
}

export interface UpdatePaymentDetailsData {
    bank_name?: string;
    bank_account_number?: string;
    bank_routing_number?: string;
    account_holder_name?: string;
    zelle_email?: string;
    zelle_phone?: string;
    paypal_email?: string;
    venmo_username?: string;
    cashapp_username?: string;
    pago_movil_bank?: string;
    pago_movil_phone?: string;
    pago_movil_id_number?: string;
    cash_instructions?: string;
}

export interface HOAAdminProfile {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    organization_name?: string;
    max_units_allowed: number;
    current_unit_count: number;
    can_add_units: boolean;
    subscription_status: string;
    grace_period_ends_at?: string | null;
}

// Get HOA Admin profile
export async function getHOAAdminProfile(): Promise<HOAAdminProfile> {
    const response = await api.get("/auth/hoa-admins/");
    // API returns array filtered to current user for HOA admins
    const profiles = response.data;
    if (!profiles || profiles.length === 0) {
        throw new Error("Profile not found");
    }
    return profiles[0];
}

// Update HOA Admin profile
export async function updateHOAAdminProfile(
    id: string,
    data: {
        first_name?: string;
        last_name?: string;
        organization_name?: string;
    }
): Promise<HOAAdminProfile> {
    const response = await api.patch(`/auth/hoa-admins/${id}/`, data);
    return response.data;
}

// Get payment details
export async function getPaymentDetails(): Promise<PaymentDetails | null> {
    try {
        const response = await api.get("/auth/payment-details/");
        // API returns array, get first item (HOA admin only has their own)
        return response.data[0] || null;
    } catch (error: any) {
        if (error?.response?.status === 404) {
            return null;
        }
        throw error;
    }
}

// Create payment details
export async function createPaymentDetails(
    data: UpdatePaymentDetailsData
): Promise<PaymentDetails> {
    const response = await api.post("/auth/payment-details/", data);
    return response.data;
}

// Update payment details
export async function updatePaymentDetails(
    id: string,
    data: UpdatePaymentDetailsData
): Promise<PaymentDetails> {
    const response = await api.patch(`/auth/payment-details/${id}/`, data);
    return response.data;
}

// Change password (authenticated user)
export async function changePassword(
    currentPassword: string,
    newPassword: string
): Promise<void> {
    await api.post("/auth/password/change_password/", {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: newPassword,
    });
}
